/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import NewPost from '@linc-technologies/linced-common/components/new-post';
import { isArray } from '@ember/array';
import { service } from '@ember/service';
import { get, action, computed } from '@ember/object';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import { filterBy } from '@linc-technologies/linced-common/utils/arrays';
import { replacePronouns } from '@linc-technologies/linced-common/utils/pronouns';
import { saveWithMedia } from '@linc-technologies/linced-common/utils/post-media';
import { copyAttachment } from '@linc-technologies/linced-common/utils/uppy';
import moment from 'moment';
import { isEmpty } from '@ember/utils';
import {
  autosaveTinyMCEConfig,
  removeAutosavedDrafts,
  autosaveCreateEditorId,
} from '@linc-technologies/linced-common/utils/tinymce';

// ember-auto-import flatpickr css.
import 'flatpickr/dist/themes/material_blue.css';

const nameRegExp = [
  new RegExp('\\b(FIRSTNAME)\\b', 'g'),
  new RegExp('\\b(LASTNAME)\\b', 'g'),
];
export default class BulkPost extends NewPost {
  @tracked
  newDate;

  @action
  setNewDate(value) {
    this.newDate = value;
  }

  @tracked
  postSubject;

  @action
  setPostSubject(value) {
    this.postSubject = value;
  }

  @tracked
  step;

  @action
  setStep(value) {
    this.step = value;
  }

  @service
  session;

  @service
  toaster;

  @tracked
  selectedPeople = [];

  allGoals = null;

  @tracked
  substituteName = true;

  @action
  setSubstituteName(value) {
    this.substituteName = value;
  }

  @tracked
  substitutePronouns = true;

  @action
  setSubstitutePronouns(value) {
    this.substitutePronouns = value;
  }

  @tracked
  isCollapsed = false;

  @computed('selectedPage.tagCategoryIds.[]')
  get pageHasTags() {
    return this.get('selectedPage.tagCategoryIds.length') > 0;
  }

  postTags = [];

  @computed('selectedPeople.[]')
  get neutralGenderIncluded() {
    return this.selectedPeople.some(
      (person) => person.get('preferredGender') === 'N',
    );
  }

  @computed('selectedPeople.[]', 'selectedPage', 'type', 'postContent')
  get formIsInvalid() {
    if (!this.selectedPage) {
      // Require a post type (page) be selected for regular Posts
      return true;
    }

    if (this.type <= 2 && !this.selectedPeople?.[0]) {
      // Require a person be chosen (except for site-wide posts)
      return true;
    }

    let postContent = this.postContent;

    return !postContent || postContent === '<p></p>';
  }

  @computed('type', 'selectedPeople.[]')
  get filteredPeople() {
    let selectedPeople = this.selectedPeople;

    return filterBy(this.store.peekAll('person'), 'type', this.type).filter(
      function (person) {
        if (selectedPeople) {
          return !selectedPeople.includes(person);
        }
        return true;
      },
    );
  }

  @computed('selectedPeople.[]', 'isCollapsed')
  get displayedPeople() {
    if (!this.isCollapsed) {
      return this.selectedPeople;
    }
    return this.selectedPeople.slice(0, 5);
  }

  @tracked
  previewLearner = null;

  @action
  setPreviewLearner(value) {
    this.previewLearner = value;
  }

  @tracked
  postContent;

  @action
  setPostContent(value) {
    this.postContent = value;
  }

  @tracked
  selectedPage;

  @tracked
  selectedPerson;

  @tracked
  saveInProgress;

  @tracked
  dialogOpen;

  @tracked
  postDate;

  @computed(
    'postContent',
    'substituteName',
    'substitutePronouns',
    'previewLearner',
  )
  get preview() {
    if (this.previewLearner) {
      return this.contentForLearner(this.previewLearner);
    }
    let wrappedToken = '<span class="reuse-post-token">$1</span>';

    // Strip existing tokens
    let content = this.postContent.replace(
      /<span class="reuse-post-token">([^<]+?)<\/span>/g,
      '$1',
    );

    // Tag pronouns (if selected for replacement)
    if (this.substitutePronouns) {
      content = this.replacePronouns(null, true);
    }

    // Tag name (if selected for replacement)
    if (this.substituteName) {
      content = content.replace(nameRegExp[0], wrappedToken);
      content = content.replace(nameRegExp[1], wrappedToken);
    }

    return content;
  }

  // TinyMCE editor
  // editorOptions returns an object for TinyMCE editor config with autosave enabled.
  // We need a unique element ID for specifying selectors and localStorage keys.
  @computed('editorId')
  get editorOptions() {
    return Object.freeze(autosaveTinyMCEConfig(this.editorId));
  }

  // editorId returns an element ID to assign to the editor's children
  @computed('session.person.id')
  get editorId() {
    let userId = this.session.person.get('id');
    return autosaveCreateEditorId(userId, null, null);
  }

  replacePronouns(targetGender, preview) {
    return replacePronouns(
      this.postContent,
      targetGender,
      this.neutralGenderIncluded,
      preview,
      'reuse-post-token',
    );
  }

  contentForLearner(learner) {
    let content = this.postContent;

    // Replace pronouns if enabled and required
    if (this.substitutePronouns) {
      let gender = learner.get('preferredGender');
      if (isEmpty(gender)) {
        gender = 'N';
      }
      content = this.replacePronouns(gender);
    }

    // Replace name if enabled
    if (this.substituteName) {
      content = content.replace(nameRegExp[0], learner.firstName);
      content = content.replace(nameRegExp[1], learner.lastName);
    }

    return content;
  }

  init() {
    super.init(...arguments);
    this.postContent = '';
  }

  @action
  removePerson(person) {
    this.selectedPeople = this.selectedPeople.filter((p) => p !== person);
  }

  @action
  selectPerson(person) {
    const selectedPeople = this.selectedPeople;
    if (selectedPeople) {
      if (!selectedPeople.includes(person)) {
        this.selectedPeople = [...selectedPeople, person];
      }
      return;
    }
    this.selectedPeople = person;
  }

  @action
  reset() {
    super.reset();
    this.selectedPeople = null;
  }

  @action
  savePosts() {
    this.saveInProgress = true;
    const selectedPeople = this.selectedPeople;
    if (selectedPeople) {
      let completedPosts = 0;
      // Bulk post creation
      selectedPeople.forEach((person, index) => {
        // Each post gets its own media records. Copy them now, before any
        // save can fail and unload an original another post copies from.
        let media = this.attachments.map(({ record }) =>
          index === 0 ? record : copyAttachment(this.store, record),
        );
        // Create a new post
        let post = this.store.createRecord(this.entityType, {
          author: this.get('session.person'),
          schoolId: this.computedSchool,
          assessment: this.assessment,
          page: this.selectedPage,
          personId: person,
          groupIds: this.selectedGroups,
          subject: this.postSubject,
          displayDate: this.postDate,
          content: '',
          tagIds: this.postTags,
          status: PostStatus.DRAFT,
          surveyId: this.survey,
        });
        post.set(
          'content',
          selectedPeople.length > 1
            ? this.contentForLearner(person)
            : this.postContent,
        );
        saveWithMedia(post, media, () =>
          this.toaster.error('Error generating attachments'),
        )
          .catch(() => {
            this.toaster.error('Error generating posts');
          })
          .finally(() => {
            completedPosts++;
            if (completedPosts === selectedPeople.length) {
              this.dialogOpen = false;
              this.saveInProgress = false;
              // Remove autosaves from the local storage
              let path = window.location.pathname;
              removeAutosavedDrafts(path, this.editorId);
              this.reset();
              this.toaster.success(
                `${completedPosts} Posts Successfully Created`,
              );
              let postCreated = this.postCreated;
              if (postCreated) {
                postCreated();
              }
            }
          });
      });
    }
  }

  initialisePostData() {
    if (isArray(this.person)) {
      // Filter (not mutate) - this.person is the @person argument passed
      // in by the caller (e.g. group/assessments/assessment.js's
      // openPostDialogue); it's never read again after being passed here,
      // so deriving selectedPeople this way instead of mutating this.person
      // in place means the caller no longer needs to hand this component a
      // genuine EmberArray.
      this.selectedPeople = this.person.filter((person) => person.get('id'));
    } else {
      if (this.person && this.person.get('id')) {
        this.selectedPeople = [this.person];
      }
    }
    if (this.selectedPeople.length >= 6) {
      this.isCollapsed = true;
    }
    this.selectedPerson = null;
    let page = this.page;
    this.postDate = moment().unix();
    if (page && page.get('id')) {
      this.selectedPage = page;
      // If a post template exists, set it as the post body content.
      this.postContent = get(page, 'postTemplate');
    }
  }
}
